import { useCallback, useEffect, useState } from "react";
import {
  Clock, CheckCircle2, AlertTriangle, XCircle, Sparkles, Mail, Truck,
  CalendarClock, FileText, Loader2, Plus, Trash2, Info,
} from "lucide-react";
import { toast } from "sonner";

function deadlinePillClass(days) {
  if (days <= 14) return "pill-bad";
  if (days <= 30) return "pill-warn";
  return "pill-ok";
}

function daysToRespondLabel(days) {
  const plural = (n) => (n === 1 ? "" : "s");
  if (days >= 0) return `${days} day${plural(days)} to respond`;
  return `Overdue by ${-days} day${plural(-days)}`;
}
import api from "@/lib/api";
import { friendlyError } from "@/lib/errors";
import { TestimonialPrompt } from "@/components/TestimonialPrompt";

/**
 * Case Timeline — single-glance chronological view of USCIS milestones.
 * Auto-populated from case creation, notices uploaded, drafts generated, and
 * user-added custom events ("response mailed", "biometrics completed", etc.).
 *
 * Mounted at the top of the Review section on CaseWorkspace.
 */

const LIFECYCLE_LABEL = {
  in_preparation:       { label: "In preparation",        pill: "pill-ai" },
  filed_with_uscis:     { label: "Filed with USCIS",      pill: "pill-warn" },
  receipted:            { label: "Receipted by USCIS",    pill: "pill-ok" },
  biometrics_scheduled: { label: "Biometrics scheduled",  pill: "pill-ai" },
  interview_scheduled:  { label: "Interview scheduled",   pill: "pill-ai" },
  rfe_pending:          { label: "RFE — response due",    pill: "pill-warn" },
  noid_pending:         { label: "NOID — response due",   pill: "pill-bad" },
  approved:             { label: "Approved",              pill: "pill-ok" },
  denied:               { label: "Denied",                pill: "pill-bad" },
};

const PILL_STYLE = {
  success: { bg: "#ECFDF5", border: "#A7F3D0", text: "#065F46", dot: "#065F46" },
  warn:    { bg: "#FEF3C7", border: "#FDE68A", text: "#92400E", dot: "#92400E" },
  danger:  { bg: "#FEE2E2", border: "#FCA5A5", text: "#991B1B", dot: "#991B1B" },
  info:    { bg: "#EEF2FF", border: "#C7D2FE", text: "#3730A3", dot: "#3730A3" },
  ai:      { bg: "#FDF8F0", border: "#EED8A1", text: "#875C12", dot: "#875C12" },
  neutral: { bg: "#F3F4F6", border: "#E5E7EB", text: "#374151", dot: "#6B7280" },
};

const ICON_FOR_KIND = {
  case_created:    Clock,
  case_filed:      Truck,
  notice_receipt:  CheckCircle2,
  notice_biometrics: CalendarClock,
  notice_interview:  CalendarClock,
  notice_rfe:      AlertTriangle,
  notice_noid:     AlertTriangle,
  notice_approval: CheckCircle2,
  notice_denial:   XCircle,
  notice_transfer: Info,
  notice_other:    FileText,
  draft_generated: Sparkles,
  response_mailed: Mail,
  biometrics_completed: CheckCircle2,
  interview_completed: CheckCircle2,
  custom:          Clock,
};

export default function TimelineCard({ caseData, onMarkFiled, onApprovalDetected }) {
  const caseId = caseData.id;
  const [tl, setTl] = useState({ events: [], lifecycle_status: "in_preparation", filed_at: null });
  const [loading, setLoading] = useState(true);
  const [showFiledModal, setShowFiledModal] = useState(false);
  const [showAddEvent, setShowAddEvent] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/cases/${caseId}/timeline`);
      setTl(data);
      // If we just detected an approval and the parent asked for a hook, fire it once
      if (data.lifecycle_status === "approved") onApprovalDetected?.();
    } catch (err) {
      toast.error(friendlyError(err, "We couldn't load the timeline."));
    } finally {
      setLoading(false);
    }
  }, [caseId, onApprovalDetected]);
  useEffect(() => { load(); }, [load]);

  const removeEvent = async (eid) => {
     
    if (!window.confirm("Remove this event from the timeline?")) return;
    try {
      await api.delete(`/cases/${caseId}/timeline/event/${eid}`);
      toast.success("Event removed.");
      load();
    } catch (err) {
      toast.error(friendlyError(err));
    }
  };

  const status = LIFECYCLE_LABEL[tl.lifecycle_status] || LIFECYCLE_LABEL.in_preparation;
  const filed = Boolean(tl.filed_at);

  return (
    <div className="card-surface p-6 mb-5" data-testid="timeline-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
             style={{ background: "var(--ai-bg)", border: "1px solid var(--ai-border)" }}>
          <Clock className="w-4 h-4" style={{ color: "var(--ai-text)" }} />
        </div>
        <div className="flex-1">
          <h3 className="font-display font-bold text-lg" style={{ color: "var(--text-primary)" }}>
            Case Timeline
          </h3>
          <p className="text-xs" style={{ color: "var(--text-secondary)" }}>
            Every USCIS milestone on this case, in one place.
          </p>
        </div>
        <span className={`pill ${status.pill}`} data-testid="timeline-status">{status.label}</span>
      </div>

      {!filed && (
        <div
          className="mb-4 p-3 rounded-md flex items-center justify-between gap-3 flex-wrap"
          style={{ background: "var(--ai-bg)", border: "1px solid var(--ai-border)" }}
          data-testid="timeline-mark-filed-banner"
        >
          <div className="text-xs" style={{ color: "var(--ai-text)" }}>
            <strong>Mailed your packet to USCIS?</strong> Mark it filed and we&apos;ll track everything from here.
          </div>
          <button type="button"
                  className="btn-primary text-xs"
                  onClick={() => setShowFiledModal(true)}
                  data-testid="timeline-mark-filed-btn">
            <Truck className="w-3.5 h-3.5 inline mr-1" /> I mailed it
          </button>
        </div>
      )}

      {loading ? (
        <div className="text-sm flex items-center gap-2" style={{ color: "var(--text-secondary)" }}>
          <Loader2 className="w-4 h-4 animate-spin" /> Loading timeline…
        </div>
      ) : tl.events.length === 0 ? (
        <div className="text-xs italic" style={{ color: "var(--text-secondary)" }}>
          Nothing yet. Your first milestone will appear once you upload a notice or mark the case filed.
        </div>
      ) : (
        <ol className="relative pl-6 space-y-4" data-testid="timeline-events">
          <div
            aria-hidden
            className="absolute left-[9px] top-1 bottom-1 w-[2px]"
            style={{ background: "var(--border-stone)" }}
          />
          {tl.events.map((ev) => (
            <TimelineEvent key={ev.id} ev={ev} onRemove={ev.removable ? () => removeEvent(ev.id) : null} />
          ))}
        </ol>
      )}

      <div className="mt-4 flex items-center gap-2">
        <button
          type="button"
          onClick={() => setShowAddEvent(true)}
          className="btn-ghost text-xs"
          data-testid="timeline-add-event-btn"
        >
          <Plus className="w-3.5 h-3.5 inline mr-1" /> Add a custom event
        </button>
      </div>

      {showFiledModal && (
        <MarkFiledModal
          onClose={() => setShowFiledModal(false)}
          onSubmit={async (payload) => {
            try {
              await api.post(`/cases/${caseId}/mark-filed`, payload);
              toast.success("Case marked as filed. Nice work!");
              setShowFiledModal(false);
              await load();
              onMarkFiled?.();
            } catch (err) {
              toast.error(friendlyError(err));
            }
          }}
        />
      )}
      {showAddEvent && (
        <AddEventModal
          onClose={() => setShowAddEvent(false)}
          onSubmit={async (payload) => {
            try {
              await api.post(`/cases/${caseId}/timeline/event`, payload);
              toast.success("Event added.");
              setShowAddEvent(false);
              await load();
            } catch (err) {
              toast.error(friendlyError(err));
            }
          }}
        />
      )}

      {/* Testimonial prompt after filing or approval — belongs here since the timeline is authoritative about lifecycle. */}
      {(tl.filed_at || tl.lifecycle_status !== "in_preparation") && tl.lifecycle_status !== "approved" && tl.lifecycle_status !== "denied" && (
        <div className="mt-5">
          <TestimonialPrompt caseId={caseId} moment="filed" />
        </div>
      )}
      {tl.lifecycle_status === "approved" && (
        <div className="mt-5">
          <TestimonialPrompt caseId={caseId} moment="decided" />
        </div>
      )}
    </div>
  );
}

function TimelineEvent({ ev, onRemove }) {
  const pill = PILL_STYLE[ev.pill] || PILL_STYLE.neutral;
  const Icon = ICON_FOR_KIND[ev.kind] || Clock;
  const deadline = ev.meta?.response_deadline;
  const days = deadline ? daysBetween(new Date(), new Date(deadline)) : null;
  const receipt = ev.meta?.uscis_receipt_number;
  return (
    <li className="relative pl-2" data-testid={`timeline-event-${ev.id}`}>
      <span
        className="absolute -left-[26px] top-0.5 w-5 h-5 rounded-full flex items-center justify-center"
        style={{ background: pill.bg, border: `1px solid ${pill.border}` }}
      >
        <Icon className="w-3 h-3" style={{ color: pill.text }} />
      </span>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display font-bold text-[13px]" style={{ color: "var(--text-primary)" }}>
              {ev.label}
            </span>
            {ev.occurred_at && (
              <span className="text-[11px]" style={{ color: "var(--text-secondary)" }}>
                {formatDate(ev.occurred_at)}
              </span>
            )}
            {receipt && (
              <span className="pill" style={{ background: "#F3F4F6", color: "#374151" }}>
                <FileText className="w-3 h-3" /> {receipt}
              </span>
            )}
            {deadline && days != null && (
              <span
                className={`pill ${deadlinePillClass(days)}`}
                data-testid="timeline-deadline-chip"
              >
                <CalendarClock className="w-3 h-3" />
                {daysToRespondLabel(days)}
              </span>
            )}
          </div>
          {ev.note && (
            <div className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>
              {ev.note}
            </div>
          )}
        </div>
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="btn-ghost text-xs"
            title="Remove this custom event"
            data-testid={`timeline-remove-${ev.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </li>
  );
}

/* ---------- Modals ---------- */

function MarkFiledModal({ onClose, onSubmit }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [tracking, setTracking] = useState("");
  const [via, setVia] = useState("USPS Priority");
  const [busy, setBusy] = useState(false);
  return (
    <Modal onClose={onClose} title="Mark this case as filed with USCIS" testId="mark-filed-modal">
      <div className="space-y-3">
        <div>
          <label className="field-label">Date you mailed it <span style={{ color: "#B91C1C" }}>*</span></label>
          <input type="date" className="field-input" value={date} required
                 onChange={(e) => setDate(e.target.value)} data-testid="mark-filed-date" />
        </div>
        <div>
          <label className="field-label">How did you send it?</label>
          <select className="field-input" value={via} onChange={(e) => setVia(e.target.value)} data-testid="mark-filed-via">
            <option>USPS Priority</option>
            <option>USPS Certified Mail</option>
            <option>USPS Priority Mail Express</option>
            <option>FedEx</option>
            <option>UPS</option>
            <option>Other</option>
          </select>
        </div>
        <div>
          <label className="field-label">Tracking number (optional)</label>
          <input className="field-input" value={tracking} onChange={(e) => setTracking(e.target.value)}
                 data-testid="mark-filed-tracking" placeholder="e.g. 9400111206213…" />
        </div>
      </div>
      <div className="mt-5 flex items-center gap-2 justify-end">
        <button type="button" onClick={onClose} className="btn-ghost text-sm">Cancel</button>
        <button
          type="button"
          disabled={busy}
          className="btn-primary text-sm"
          onClick={async () => {
            setBusy(true);
            await onSubmit({
              filed_at: date, tracking_number: tracking || null, mailed_via: via,
            });
            setBusy(false);
          }}
          data-testid="mark-filed-submit"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> : null}
          Mark as filed
        </button>
      </div>
    </Modal>
  );
}

const EVENT_KIND_OPTIONS = [
  ["response_mailed", "Response to USCIS mailed"],
  ["biometrics_completed", "Biometrics completed"],
  ["interview_completed", "Interview completed"],
  ["custom", "Other event"],
];

function AddEventModal({ onClose, onSubmit }) {
  const [kind, setKind] = useState("response_mailed");
  const [label, setLabel] = useState(EVENT_KIND_OPTIONS[0][1]);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <Modal onClose={onClose} title="Add a timeline event" testId="add-event-modal">
      <div className="space-y-3">
        <div>
          <label className="field-label">Event type</label>
          <select
            className="field-input"
            value={kind}
            onChange={(e) => {
              const v = e.target.value;
              setKind(v);
              const preset = EVENT_KIND_OPTIONS.find(([k]) => k === v);
              if (preset && v !== "custom") setLabel(preset[1]);
            }}
            data-testid="add-event-kind"
          >
            {EVENT_KIND_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
        <div>
          <label className="field-label">Label</label>
          <input className="field-input" value={label} onChange={(e) => setLabel(e.target.value)}
                 data-testid="add-event-label" />
        </div>
        <div>
          <label className="field-label">Date</label>
          <input type="date" className="field-input" value={date}
                 onChange={(e) => setDate(e.target.value)} data-testid="add-event-date" />
        </div>
        <div>
          <label className="field-label">Note (optional)</label>
          <textarea className="field-input text-xs" rows={2} value={note}
                    onChange={(e) => setNote(e.target.value)} data-testid="add-event-note" />
        </div>
      </div>
      <div className="mt-5 flex items-center gap-2 justify-end">
        <button type="button" onClick={onClose} className="btn-ghost text-sm">Cancel</button>
        <button
          type="button"
          disabled={busy}
          className="btn-primary text-sm"
          onClick={async () => {
            setBusy(true);
            await onSubmit({ kind, label, occurred_at: date, note: note || null });
            setBusy(false);
          }}
          data-testid="add-event-submit"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> : null}
          Add event
        </button>
      </div>
    </Modal>
  );
}

function Modal({ onClose, title, testId, children }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(26,46,36,0.55)" }}
      onClick={onClose}
      data-testid={testId}
    >
      <div
        className="card-surface p-6 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-display font-bold text-lg mb-4" style={{ color: "var(--text-primary)" }}>
          {title}
        </h3>
        {children}
      </div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[parseInt(m, 10) - 1]} ${parseInt(d, 10)}, ${y}`;
}

function daysBetween(from, to) {
  if (!(to instanceof Date) || isNaN(to)) return null;
  const ms = to.getTime() - from.getTime();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}
